import { useCallback, useEffect, useState } from 'react';
import { Ban, ChevronLeft, CloudCheck, FileUp, Lock, Users, type LucideIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useServices } from '../app/services';
import { APP_STORE_URL, PRIVACY_URL, TERMS_URL, openExternal } from '../app/links';
import { formatLongDate } from '../i18n/format';
import { Button, ButtonLink, Card, ChoiceList, IconButton, List, ListRow, LoadingState, ScreenTitle, Section, useToast } from '../ui';
import { PREMIUM_FEATURES, type PlanId, type PremiumFeature, type PremiumPlan } from './entitlement';
import { usePremium } from './PremiumProvider';
import './premium.css';

export const FEATURE_ICON: Record<PremiumFeature, LucideIcon> = { household: Users, sync: CloudCheck, billImport: FileUp, noAds: Ban };

const isFeature = (v: string | null): v is PremiumFeature => (PREMIUM_FEATURES as readonly string[]).includes(v ?? '');

/** The stores' own subscription pages, when RevenueCat does not give one. */
const MANAGE_URL: Record<'ios' | 'android', string> = {
  ios: 'https://apps.apple.com/account/subscriptions',
  android: 'https://play.google.com/store/account/subscriptions',
};

/**
 * What Premium adds (the feature someone tapped comes first), and buying
 * it: the store's own prices, monthly or yearly, what renewing means, the
 * terms, and restoring a purchase. Nothing already on the device is ever
 * locked by not buying.
 */
export function PremiumScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const premium = usePremium();
  const [params] = useSearchParams();
  const asked = params.get('for');
  const order = isFeature(asked) ? [asked, ...PREMIUM_FEATURES.filter((f) => f !== asked)] : PREMIUM_FEATURES;

  return (
    <>
      <div className="screen-back">
        <IconButton icon={ChevronLeft} label={t('premium.back')} onClick={() => navigate(-1)} />
      </div>
      <ScreenTitle>{t('premium.title')}</ScreenTitle>
      <p className="premium-lede">{t('premium.lede')}</p>

      <Section title={t('premium.adds')} divided={false}>
        <Card flush className="premium-wrap">
          <List inset>
            {order.map((f) => {
              const Icon = FEATURE_ICON[f];
              return (
                <ListRow key={f} leading={<Icon size={18} aria-hidden />} title={t(`premium.feature.${f}.title`)} subtitle={t(`premium.feature.${f}.body`)} />
              );
            })}
          </List>
        </Card>
      </Section>

      {!premium.ready ? <LoadingState rows={2} /> : premium.active ? <HavePremium /> : <Paywall back={isFeature(asked)} />}
    </>
  );
}

/** Premium is on: until when, and where to change or cancel it. */
function HavePremium() {
  const { t, i18n } = useTranslation();
  const { storefront, platform } = useServices();
  const premium = usePremium();
  const until = premium.expiresAt ? formatLongDate(premium.expiresAt, i18n.language) : null;
  const manage = async () => {
    const url = (await storefront?.manageUrl()) ?? (platform === 'web' ? null : MANAGE_URL[platform]);
    if (url) openExternal(url);
  };
  return (
    <div className="premium-have stack stack-tight" data-testid="premium-status">
      <p className="premium-have-title">{t('premium.active')}</p>
      {until && <p className="premium-note">{premium.willRenew === false ? t('premium.endsOn', { date: until }) : t('premium.renewsOn', { date: until })}</p>}
      {premium.source === 'account' && <p className="premium-note">{t('premium.fromAccount')}</p>}
      {platform !== 'web' && premium.source === 'store' && (
        <Button variant="secondary" size="lg" block onClick={() => void manage()}>
          {t('premium.manage')}
        </Button>
      )}
    </div>
  );
}

type Plans = { state: 'loading' } | { state: 'none' } | { state: 'ready'; plans: PremiumPlan[] };

/** Buying: on a phone, from the store; on the web, where to get it. */
function Paywall({ back }: { back: boolean }) {
  const { t } = useTranslation();
  const { storefront, platform } = useServices();
  const premium = usePremium();
  const toast = useToast();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plans>({ state: 'loading' });
  const [chosen, setChosen] = useState<PlanId>('yearly');
  const [busy, setBusy] = useState<'buy' | 'restore' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!storefront) return;
    setPlans({ state: 'loading' });
    const list = await storefront.plans();
    setPlans(list.length ? { state: 'ready', plans: list } : { state: 'none' });
    if (list.length && !list.some((p) => p.id === 'yearly')) setChosen(list[0]!.id);
  }, [storefront]);
  useEffect(() => void load(), [load]);

  if (!storefront || platform === 'web') {
    return (
      <div className="premium-buy stack stack-tight" data-testid="premium-web">
        <p className="premium-have-title">{t('premium.web.title')}</p>
        <p className="premium-note">{t('premium.web.body')}</p>
        <Button variant="primary" size="lg" block onClick={() => openExternal(APP_STORE_URL)}>
          {t('premium.web.appStore')}
        </Button>
      </div>
    );
  }

  const welcome = async () => {
    await premium.refresh();
    toast.show({ message: t('premium.welcome') });
    // Came from a locked feature: back to it, now open.
    if (back) navigate(-1);
  };

  const buy = async () => {
    setBusy('buy');
    setMessage(null);
    const outcome = await storefront.buy(chosen);
    setBusy(null);
    if (outcome.ok) return welcome();
    if (outcome.reason !== 'cancelled') setMessage(t(`premium.outcome.${outcome.reason}`));
  };

  const restore = async () => {
    setBusy('restore');
    setMessage(null);
    const e = await storefront.restore();
    setBusy(null);
    if (e.active) return welcome();
    setMessage(t(platform === 'ios' ? 'premium.restoreNone.ios' : 'premium.restoreNone.android'));
  };

  const list = plans.state === 'ready' ? plans.plans : [];
  const monthly = list.find((p) => p.id === 'monthly');
  const yearly = list.find((p) => p.id === 'yearly');
  // What a year costs against twelve months, when both are in the same currency.
  const saving =
    monthly && yearly && monthly.currency === yearly.currency && monthly.amount > 0 ? Math.round((1 - yearly.amount / (monthly.amount * 12)) * 100) : 0;
  const plan = list.find((p) => p.id === chosen);

  return (
    <div className="premium-buy stack" data-testid="premium-paywall">
      {plans.state === 'loading' && <LoadingState rows={2} />}
      {plans.state === 'none' && (
        <div className="stack stack-tight">
          <p className="premium-note" role="alert">
            {t('premium.storeUnavailable')}
          </p>
          <Button variant="secondary" size="lg" block onClick={() => void load()}>
            {t('premium.retry')}
          </Button>
        </div>
      )}
      {plans.state === 'ready' && (
        <>
          <ChoiceList<PlanId>
            label={t('premium.choosePlan')}
            value={chosen}
            onChange={setChosen}
            options={list.map((p) => ({
              value: p.id,
              label: t(`premium.plan.${p.id}`),
              description:
                p.id === 'yearly'
                  ? [p.perMonth && t('premium.perMonth', { price: p.perMonth }), saving > 0 && t('premium.save', { percent: saving })]
                      .filter(Boolean)
                      .join(' · ') || undefined
                  : t('premium.monthlyBody'),
              meta: <span className="premium-price">{t(`premium.pricePer.${p.id}`, { price: p.price })}</span>,
            }))}
          />
          <Button
            variant="primary"
            size="lg"
            block
            loading={busy === 'buy'}
            disabled={busy !== null || !plan}
            onClick={() => void buy()}
            data-testid="premium-buy"
          >
            {plan ? t('premium.buy', { price: t(`premium.pricePer.${plan.id}`, { price: plan.price }) }) : t('premium.title')}
          </Button>
        </>
      )}
      {message && (
        <p className="premium-note" role="alert" data-testid="premium-message">
          {message}
        </p>
      )}
      <Button variant="ghost" size="lg" block loading={busy === 'restore'} disabled={busy !== null} onClick={() => void restore()}>
        {t('premium.restore')}
      </Button>
      <p className="premium-fine">{t(platform === 'ios' ? 'premium.terms.ios' : 'premium.terms.android')}</p>
      <p className="premium-links">
        <button type="button" className="link-button" onClick={() => openExternal(TERMS_URL)}>
          {t('premium.termsLink')}
        </button>
        <button type="button" className="link-button" onClick={() => openExternal(PRIVACY_URL)}>
          {t('more.privacy')}
        </button>
      </p>
    </div>
  );
}

/** A small "Premium" beside a row title, while this device has no Premium. */
export function PremiumTag() {
  const { t } = useTranslation();
  const premium = usePremium();
  if (!premium.ready || premium.active) return null;
  return <span className="premium-tag">{t('premium.tag')}</span>;
}

/** In place of a Premium feature: what it does, and the way to Premium. */
export function PremiumLocked({ feature }: { feature: PremiumFeature }) {
  const { t } = useTranslation();
  const Icon = FEATURE_ICON[feature];
  return (
    <div className="premium-locked" data-testid="premium-locked" data-feature={feature}>
      <span className="premium-locked-icon" aria-hidden>
        <Icon size={24} strokeWidth={1.75} />
        <Lock size={14} strokeWidth={2.25} className="premium-locked-lock" />
      </span>
      <h2 className="premium-locked-title">{t(`premium.feature.${feature}.title`)}</h2>
      <p className="premium-locked-body">{t(`premium.locked.${feature}`)}</p>
      <ButtonLink to={`/premium?for=${feature}`} variant="primary" size="lg">
        {t('premium.see')}
      </ButtonLink>
    </div>
  );
}
